import { Box, Button, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useStyles } from './styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IEvent } from '@interfaces/event';
import ParticipantsService from '@services/ParticipantsService';
import { getClientSpecificSDCarouselEventsTransform } from '@helpers/clientSpecificUtils';
import { useNotification } from '@components/Notification';
import { useClientSpecificNoEventsNotification } from '@hooks/clientSpecificUtils';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useLocation } from 'react-router-dom';
import { compareTokenHash, getExternalUrlHash } from '@helpers/externalUrlHash';
import LoadingProgress from '@components/LoadingProgress';
import { SkyLinkEventItem } from '@core/dashboard/components/DashboardContent/SkyLink/SkyLinkEventItem';
import { useTranslation } from 'react-i18next';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { AnalyticsType } from '@interfaces/analytics';

export const SkyLink = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const [events, setEvents] = useState<IEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const { showNotification, getBadRequestNotification } = useNotification();
  const showNoEventsNotification = useClientSpecificNoEventsNotification();
  const { user, token, sendJSONMessageToWebSocket, trackAnalytics } =
    useMytaverse();
  const { pathname } = useLocation();
  const { setIsSkyLinkActive, currentEventId } = useMytaverseEvent();
  const hasLoadedEventsRef = useRef(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);

    const participantEvents =
      await ParticipantsService.getCurrentParticipantEvents();

    if (
      participantEvents.events.length === 0 &&
      user &&
      showNoEventsNotification
    ) {
      const message = `The email address ${
        user.hrData?.personalEmail || ''
      } is not invited to any worlds. Are you sure this is the correct email address?`;
      showNotification(getBadRequestNotification({ message }));

      console.error(message);

      setLoading(false);

      return;
    }

    if (participantEvents.events.length !== 0) {
      const events = await Promise.all(
        participantEvents.events
          .map((participantEvent) => ({
            ...(participantEvent.event as IEvent),
            isDisabled: participantEvent.isDisabled,
            state: participantEvent.state,
            inviteStartTime:
              participantEvent.startDate ||
              participantEvent.event?.startTime ||
              0,
          }))
          .filter(
            (event) =>
              event.status === 'ACTIVE' || event.status === 'SCHEDULED',
          )
          .filter((event) => !event.eventTags?.includes('LINUX'))
          .filter(
            (event) =>
              event.eventTags?.includes('A380') ||
              event.eventTags?.includes('FullTimeSEPHub'),
          ),
      );

      getClientSpecificSDCarouselEventsTransform(events, user);

      setEvents(events);
    }

    setLoading(false);
  }, [setLoading, setEvents, pathname, user]);

  const handleBackButton = useCallback(() => {
    setIsSkyLinkActive(false);
    sendJSONMessageToWebSocket({
      action: 'CLOSE_SKYLINK',
    });
  }, []);

  useEffect(() => {
    if (!hasLoadedEventsRef.current) {
      hasLoadedEventsRef.current = true;
      loadEvents();
    }
  }, [loadEvents]);

  useEffect(() => {
    const externalEventTokenHash = getExternalUrlHash();

    if (externalEventTokenHash) {
      compareTokenHash(token, externalEventTokenHash).then(
        (isTokenHashValid) => {
          if (!isTokenHashValid) {
            const message = 'External event auth token hash is invalid!';
            showNotification(getBadRequestNotification({ message }));
          }
        },
      );
    }
  }, []);

  return (
    <>
      <Box sx={classes.root}>
        {loading ? (
          <LoadingProgress height="30vh" />
        ) : (
          <Box sx={classes.content}>
            <Button
              onClick={handleBackButton}
              startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 20 }} />}
              sx={classes.backButton}
            >
              {translate('skyLink.back')}
            </Button>
            <Box>
              {events.length > 0 && (
                <Typography sx={classes.title}>
                  {translate('skyLink.title')}
                </Typography>
              )}
              {events.length > 0 && (
                <Typography sx={classes.subtitle}>
                  {translate('skyLink.subtitle')}
                </Typography>
              )}
            </Box>
            <Box sx={classes.scrollRow}>
              {events.length === 0 ? (
                <Typography sx={classes.subtitle}>
                  {translate('skyLink.noEligibleEvents')}
                </Typography>
              ) : (
                events.map((e) => {
                  return (
                    <SkyLinkEventItem
                      event={e}
                      key={e.eventId}
                      onClick={async () => {
                        await trackAnalytics(
                          AnalyticsType.TeleportToEventStart,
                          {
                            eventId: e.eventId,
                            sourceEventId: currentEventId,
                          },
                        );

                        let targetPath = pathname.match(/\/events\//)
                          ? pathname.replace(
                              /(\/events\/)[^/]+/,
                              `$1${e.eventId}`,
                            )
                          : `/events/${e.eventId}`;

                        if (
                          e.eventTags &&
                          e.eventTags.includes('External') &&
                          e.externalUrl
                        ) {
                          targetPath = e.externalUrl;
                        } else {
                          targetPath = `${targetPath}?skylink=true`;
                        }

                        window.open(targetPath, '_self');
                      }}
                    />
                  );
                })
              )}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};
